import { useRef, useState, useCallback } from "react";
import { Upload, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatFileSize, fileIcon } from "@/hooks/useEntityFiles";

interface LocalFileDropZoneProps {
  files: File[];
  onChange: (files: File[]) => void;
}

/**
 * Queues files locally before the entity exists.
 * Used in create dialogs — files are uploaded after the item is created.
 */
export function LocalFileDropZone({ files, onChange }: LocalFileDropZoneProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDraggingOver, setIsDraggingOver] = useState(false);

  const addFiles = useCallback(
    (incoming: File[]) => {
      onChange([...files, ...incoming]);
    },
    [files, onChange]
  );

  const removeFile = (index: number) => {
    onChange(files.filter((_, i) => i !== index));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    addFiles(Array.from(e.target.files || []));
    e.target.value = "";
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDraggingOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsDraggingOver(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDraggingOver(false);
    addFiles(Array.from(e.dataTransfer.files));
  };

  return (
    <div className="space-y-2">
      {/* Queued file list */}
      {files.length > 0 && (
        <div className="divide-y divide-border rounded-md border border-border overflow-hidden">
          {files.map((file, i) => (
            <div key={i} className="flex items-center gap-3 px-3 py-2 bg-card">
              <span className="text-lg shrink-0">{fileIcon(file.type)}</span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{file.name}</p>
                <p className="text-xs text-muted-foreground">{formatFileSize(file.size)}</p>
              </div>
              <button
                type="button"
                onClick={() => removeFile(i)}
                className="text-muted-foreground hover:text-destructive transition-colors shrink-0"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Drop zone */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className={cn(
          "flex flex-col items-center justify-center gap-1.5 rounded-md border-2 border-dashed py-5 transition-colors cursor-pointer select-none",
          isDraggingOver
            ? "border-primary bg-primary/5 text-primary"
            : "border-border text-muted-foreground hover:border-primary/50 hover:bg-muted/30"
        )}
      >
        <Upload className="h-4 w-4" />
        <p className="text-xs">
          {isDraggingOver
            ? "Drop to add"
            : files.length === 0
            ? "Drag & drop files here, or click to browse"
            : "Add more files"}
        </p>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="hidden"
        onChange={handleFileChange}
      />
    </div>
  );
}
